// What a plan's body lists, and its phases, read as the server reads them
// (crates/poulpe-server/src/plans/listing.rs): for the mock, which derives a
// plan's `issues` from its body as the server does, and for Add to plan,
// which offers a plan's phases.
//
// - HTML: an `li` inside an element of class `pp-issues` lists the key it
//   begins with (parse.ts, `planKeys`); its phase is the `section.pp-phase`
//   around it, named by its `h3`.
// - Markdown: a key in a list item lists, outside fenced code and code spans;
//   a phase is a `###` heading, or a `##` one in a plan that has no `###`.
//
// An issue listed twice keeps its first place. The mock's insertion
// (`withItem`) follows the server's: under the last list of the first phase
// whose heading begins with the section, whatever its case, or in a new phase.
// So do its taking out and moving (`unlisted`, `moved`), on the cases both
// share (crates/poulpe-server/src/plans/placing.json).
import type { PlanListing } from '../api/types'
import { keysIn } from '../lib/keys'
import { isHtmlBody } from './format'
import { hasClass, inert, isA, leadingKey, walk } from './parse'

/** The most characters of a phase's name a listing keeps. */
export const PHASE_NAME_MAX = 120

const words = (text: string) => text.replace(/\s+/g, ' ').trim()

// ---------------------------------------------------------------- Markdown

const ITEM = /^\s*(?:>\s*)*(?:[-*+]|\d{1,9}[.)])(?:\s|$)/
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?[ \t]*#*[ \t]*$/
const FENCE = /^ {0,3}(`{3,}|~{3,})/
/** A thematic break: three `-`, `*` or `_` at least, with spaces between. */
const isRule = (line: string) => /^ {0,3}[-*_][-*_ ]*$/.test(line) && line.replace(/ /g, '').length >= 3
/** A line that starts a block of its own, and so ends an item it does not continue by its indentation. */
const BLOCK_START = /^ {0,3}[#>|]/

type Line = { kind: 'heading'; level: number; text: string } | { kind: 'item'; text: string } | { kind: 'text'; text: string } | { kind: 'blank' } | { kind: 'code'; closes: boolean } | { kind: 'rule' }

/** Each line of a Markdown body, as the listing reads it: inside a fence it is code. */
function lines(body: string | readonly string[]): Line[] {
  const out: Line[] = []
  let fence: string | null = null
  for (const line of typeof body === 'string' ? body.split(/\r\n|\r|\n/) : body) {
    const opens = FENCE.exec(line)
    if (fence !== null) {
      const closes = !!opens && opens[1]![0] === fence[0] && opens[1]!.length >= fence.length && !line.slice(opens[0].length).trim()
      if (closes) fence = null
      out.push({ kind: 'code', closes })
      continue
    }
    if (opens) {
      fence = opens[1]!
      out.push({ kind: 'code', closes: false })
      continue
    }
    const heading = HEADING.exec(line)
    if (!line.trim()) out.push({ kind: 'blank' })
    else if (heading) out.push({ kind: 'heading', level: heading[1]!.length, text: words(heading[2] ?? '') })
    else if (isRule(line)) out.push({ kind: 'rule' })
    else if (ITEM.test(line)) out.push({ kind: 'item', text: line })
    else out.push({ kind: 'text', text: line })
  }
  return out
}

/** A line's text without its code spans. */
function withoutCode(text: string): string {
  return text.replace(/(`+)[\s\S]*?\1/g, ' ')
}

/** A line's text with each code span made spaces: keys found in it are where they are in the line. */
function maskCode(text: string): string {
  return text.replace(/(`+)[\s\S]*?\1/g, (span) => ' '.repeat(span.length))
}

const indented = (row: string) => /^[ \t]/.test(row)

/** How far a line is indented, a tab reaching the next multiple of four. */
function indent(row: string): number {
  let width = 0
  for (const c of row) {
    if (c === ' ') width++
    else if (c === '\t') width += 4 - (width % 4)
    else break
  }
  return width
}

/**
 * Where the item whose marker line is `at` ends, as the listing reads it: after the last line that continues it.
 * An indented text line continues it, even after blank lines, and so do an indented fence with its code, an
 * indented heading below the phases' levels (`####` and deeper), a thematic break more indented than its marker
 * (markdown-it renders it inside the item), and a nested item, more indented than its marker,
 * that names no key (one that names a key is listed on its own and keeps its place). An unindented line continues
 * it lazily only straight after it, when it starts no block of its own.
 */
function markdownItemEnd(rows: readonly string[], read: readonly Line[], at: number, names: readonly string[]): number {
  const depth = indent(rows[at]!)
  let end = at + 1
  let afterBlank = false
  for (let next = at + 1; next < rows.length;) {
    const line = read[next]!
    const row = rows[next]!
    if (line.kind === 'blank') {
      afterBlank = true
      next++
    } else if (line.kind === 'code' && indented(row)) {
      next++
      while (next < rows.length && read[next]!.kind === 'code') if ((read[next++] as { closes: boolean }).closes) break
      end = next
    } else if (
      (line.kind === 'text' && (indented(row) || !(afterBlank || BLOCK_START.test(row)))) ||
      (line.kind === 'heading' && line.level > 3 && indented(row)) ||
      (line.kind === 'rule' && indent(row) > depth) ||
      (line.kind === 'item' && indent(row) > depth && keysIn(maskCode(row), names).length === 0)
    ) {
      afterBlank = false
      end = ++next
    } else break
  }
  return end
}

/** The first of `count` candidates that `named` names: exactly first, so that a phase is never taken for a longer one it begins, else by its beginning. */
function pickPhase(count: number, named: (at: number, exact: boolean) => boolean): number {
  for (const exact of [true, false]) for (let at = 0; at < count; at++) if (named(at, exact)) return at
  return -1
}

/** Whether a phase's name names `wanted`, whatever its case and spacing: is it when `exact`, else begins with it. */
function namesPhase(name: string, wanted: string, exact: boolean): boolean {
  const [have, want] = [words(name).toLowerCase(), words(wanted).toLowerCase()]
  return exact ? have === want : have.startsWith(want)
}

function markdownListing(body: string, names: readonly string[]): PlanListing[] {
  const read = lines(body)
  const level = read.some((line) => line.kind === 'heading' && line.level === 3) ? 3 : 2
  const found: PlanListing[] = []
  const seen = new Set<string>()
  let section: string | null = null
  let inItem = false
  let afterBlank = true
  for (const line of read) {
    if (line.kind === 'heading') {
      inItem = false
      afterBlank = false
      if (line.level === level) section = line.text.slice(0, PHASE_NAME_MAX) || null
      else if (line.level < level) section = null
      continue
    }
    if (line.kind === 'code') continue
    if (line.kind === 'blank') {
      afterBlank = true
      continue
    }
    const blank = afterBlank
    afterBlank = false
    if (line.kind === 'rule') {
      inItem = false
      continue
    }
    if (line.kind === 'item') inItem = true
    // An indented line continues the item; another ends it after a blank line, or when it starts a block.
    else if (!/^[ \t]/.test(line.text) && (blank || BLOCK_START.test(line.text))) inItem = false
    if (!inItem) continue
    for (const { key } of keysIn(withoutCode(line.text), names)) {
      if (seen.has(key)) continue
      seen.add(key)
      found.push({ key, section })
    }
  }
  return found
}

function markdownPhases(body: string): string[] {
  const read = lines(body)
  const level = read.some((line) => line.kind === 'heading' && line.level === 3) ? 3 : 2
  return read.flatMap((line) => (line.kind === 'heading' && line.level === level && line.text ? [line.text.slice(0, PHASE_NAME_MAX)] : []))
}

// ---------------------------------------------------------------- HTML

function htmlListing(body: string, names: readonly string[]): PlanListing[] {
  const found: PlanListing[] = []
  const seen = new Set<string>()
  const phases: { element: Element; title: string | null }[] = []
  let inside = 0
  walk(inert(body), {
    enter(element) {
      const name = element.localName
      if (name === 'code' || name === 'pre' || name === 'script' || name === 'style' || name === 'template' || name === 'noscript') return false
      if (isA(element, 'section', 'pp-phase')) phases.push({ element, title: null })
      const phase = phases.at(-1)
      if (name === 'h3' && phase && phase.title === null && element.parentElement === phase.element) phase.title = words(element.textContent ?? '').slice(0, PHASE_NAME_MAX)
      if (inside > 0 && name === 'li') {
        const key = leadingKey(element, names)?.key
        if (key && !seen.has(key)) {
          seen.add(key)
          found.push({ key, section: phases.at(-1)?.title || null })
        }
      }
      if (hasClass(element, 'pp-issues')) inside++
      return true
    },
    leave(element) {
      if (hasClass(element, 'pp-issues')) inside--
      if (phases.at(-1)?.element === element) phases.pop()
    },
  })
  return found
}

// ---------------------------------------------------------------- both

/** The issues a body lists, in its order, each with its phase: what the server derives into a plan's `issues`. */
export function planListing(body: string, names: readonly string[]): PlanListing[] {
  return isHtmlBody(body) ? htmlListing(body, names) : markdownListing(body, names)
}

/** A body's phases, by name, in its order: those Add to plan offers. */
export function planPhases(body: string): string[] {
  if (!isHtmlBody(body)) return markdownPhases(body)
  // Each `section.pp-phase` by its `h3`, as the server finds them: a body off the page's structure has phases too.
  const phases: string[] = []
  walk(inert(body), {
    enter(element) {
      if (isA(element, 'section', 'pp-phase')) {
        const heading = [...element.children].find((child) => child.localName === 'h3')
        const title = heading ? words(heading.textContent ?? '').slice(0, PHASE_NAME_MAX) : ''
        if (title) phases.push(title)
      }
      return true
    },
  })
  return phases
}

/** A phase's name as the person names it: a leading run of `#` dropped, and the white space around; null when nothing is left. */
export function sectionName(section: string): string | null {
  const name = section.trim().replace(/^#+/, '').trim()
  return name ? name : null
}

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;')

/** The HTML body with `li`, an item's whole markup, added as the server adds one: for the mock. */
function htmlWithLi(body: string, li: string, section: string | null): string {
  const item = `${li}\n`
  const scan = masked(body)
  // Each phase's span in the raw text: from its opening tag to the `</section>` that closes it.
  const open = /<section\b[^>]*\bclass\s*=\s*["'][^"']*\bpp-phase\b(?!s)[^"']*["'][^>]*>/gi
  const phases: { start: number; end: number; title: string }[] = []
  for (const found of scan.matchAll(open)) {
    let depth = 1
    const tags = /<\/?section\b[^>]*>/gi
    tags.lastIndex = found.index + found[0].length
    let end = body.length
    for (let tag = tags.exec(scan); tag; tag = tags.exec(scan)) {
      depth += tag[0][1] === '/' ? -1 : 1
      if (depth === 0) { end = tag.index; break }
    }
    const inner = scan.slice(found.index, end)
    const heading = /<h3\b[^>]*>([\s\S]*?)<\/h3>/i.exec(inner)
    const title = heading ? words(body.slice(found.index + heading.index, found.index + heading.index + heading[0].length).replace(COMMENT, '').replace(/<[^>]*>/g, '')) : ''
    phases.push({ start: found.index, end, title })
  }
  const phase = section === null ? phases.at(-1) : phases[pickPhase(phases.length, (at, exact) => namesPhase(phases[at]!.title, section, exact))]
  if (phase) {
    const inner = scan.slice(phase.start, phase.end)
    const lists = [...inner.matchAll(/<ul\b[^>]*\bclass\s*=\s*["'][^"']*\bpp-issues\b[^"']*["'][^>]*>/gi)]
    const list = lists.at(-1)
    if (list) {
      const close = inner.indexOf('</ul>', list.index)
      if (close >= 0) return splice(body, phase.start + close, item)
    }
    return splice(body, phase.end, `<ul class="pp-issues">\n${item}</ul>\n`)
  }
  const added = `<section class="pp-phase">\n<h3>${escapeHtml(section ?? 'To place')}</h3>\n<ul class="pp-issues">\n${item}</ul>\n</section>\n`
  const after = phases.at(-1)
  if (after) return splice(body, scan.indexOf('>', after.end) + 1, `\n${added}`)
  return `${body.replace(/\s*$/, '')}\n${added}`
}

function splice(body: string, at: number, text: string): string {
  const before = body.slice(0, at)
  return `${before}${before && !before.endsWith('\n') ? '\n' : ''}${text}${body.slice(at)}`
}

/** The Markdown body with `item` added, under the phase that `section` begins, or a new `###` one. */
function markdownWith(body: string, item: string, section: string | null, names: readonly string[]): string {
  const rows = body.replace(/\s+$/, '').split('\n')
  if (rows.length === 1 && rows[0] === '') rows.pop()
  const read = lines(rows.join('\n'))
  if (section === null) return `${[...rows, ...(read.at(-1)?.kind === 'item' ? [] : rows.length ? [''] : []), item].join('\n')}\n`
  const start = pickPhase(read.length, (at, exact) => {
    const line = read[at]!
    return line.kind === 'heading' && namesPhase(line.text, section, exact)
  })
  if (start < 0) return `${[...rows, ...(rows.length ? [''] : []), `### ${words(section)}`, '', item].join('\n')}\n`
  const level = (read[start] as { level: number }).level
  let end = read.findIndex((line, at) => at > start && line.kind === 'heading' && line.level <= level)
  if (end < 0) end = read.length
  let last = -1
  for (let at = start + 1; at < end; at++) if (read[at]!.kind === 'item') last = at
  if (last >= 0) {
    rows.splice(Math.min(markdownItemEnd(rows, read, last, names), end), 0, item)
  } else {
    rows.splice(start + 1, 0, '', item)
  }
  return `${rows.join('\n')}\n`
}

/** The body once it lists `key`: the mock's `list_in_plan`. The new item goes after the last one's own lines, nested items among them, whichever keys `names` gives. */
export function withItem(body: string, key: string, title: string, section: string | null, names: readonly string[] = []): string {
  return isHtmlBody(body) ? htmlWithLi(body, `<li>${escapeHtml(key)} ${escapeHtml(words(title))}</li>`, section) : markdownWith(body, `- ${key} ${words(title)}`, section, names)
}

/** What taking an issue out of a body, or moving it, comes to, as the server answers (listing.rs, `Placed`). */
export type Placed = { body: string } | 'not_listed' | 'refused'

/** The body without the Markdown items whose first key is `key`, `key` escaped where a kept item names it later; and the first item dropped, as `- text`. */
function markdownWithout(body: string, key: string, names: readonly string[]): [string, string | null] {
  const rows = body.split('\n')
  const read = lines(rows)
  const out: string[] = []
  let moved: string | null = null
  for (let at = 0; at < rows.length;) {
    if (read[at]!.kind !== 'item') {
      out.push(rows[at]!)
      at++
      continue
    }
    const end = markdownItemEnd(rows, read, at, names)
    const item = rows.slice(at, end)
    // Fenced code names no key.
    const found = item.map((row, line) => (read[at + line]!.kind === 'code' ? [] : keysIn(maskCode(row), names)))
    if (found.flat()[0]?.key === key) {
      moved ??= [`- ${rows[at]!.replace(/^\s*(?:>\s*)*(?:[-*+]|\d{1,9}[.)])\s*/, '')}`, ...item.slice(1)].join('\n')
    } else {
      item.forEach((row, line) => {
        let escaped = ''
        let from = 0
        for (const match of found[line]!.filter((match) => match.key === key)) {
          const hyphen = match.index + row.slice(match.index, match.index + match.length).indexOf('-')
          escaped += `${row.slice(from, hyphen)}\\`
          from = hyphen
        }
        out.push(escaped + row.slice(from))
      })
    }
    at = end
  }
  return [out.join('\n'), moved]
}

const HIDDEN_ELEMENT = /<(code|pre|script|style|template|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi
const COMMENT = /<!--[\s\S]*?(?:--!?>|$)/g

/** The body with its comments, and the elements whose markup lists nothing, made spaces: where to look for tags, at the body's own places. */
function masked(body: string): string {
  const blank = (span: string) => span.replace(/[^\n]/g, ' ')
  return body.replace(COMMENT, blank).replace(HIDDEN_ELEMENT, blank)
}

/** Where the `li` whose start tag ends at `from` ends: its end tag, or the next item or end of list at its own level. */
function itemEnd(body: string, from: number): number {
  const tags = /<(\/?)(li|ul|ol)\b[^>]*>/gi
  tags.lastIndex = from
  let depth = 0
  for (let tag = tags.exec(body); tag; tag = tags.exec(body)) {
    const closing = tag[1] === '/'
    const list = tag[2]!.toLowerCase() !== 'li'
    if (depth === 0 && (!list || closing)) return tag.index
    if (list) depth += closing ? -1 : 1
  }
  return body.length
}

/** The spans of the elements of class `pp-issues`, each from its start tag to its end tag. */
function issueSpans(body: string): [number, number][] {
  const spans: [number, number][] = []
  for (const found of body.matchAll(/<([a-z][a-z0-9]*)\b[^>]*\bclass\s*=\s*["'][^"']*\bpp-issues\b[^"']*["'][^>]*>/gi)) {
    const name = found[1]!
    const tags = new RegExp(`<(/?)${name}\\b[^>]*>`, 'gi')
    tags.lastIndex = found.index + found[0].length
    let depth = 1
    let end = body.length
    for (let tag = tags.exec(body); tag; tag = tags.exec(body)) {
      depth += tag[1] === '/' ? -1 : 1
      if (depth === 0) { end = tag.index; break }
    }
    spans.push([found.index, end])
  }
  return spans
}

/** The HTML body without the listing `li`s that begin with `key`, each through its end tag and with its line when it stands on one of its own; and the first of them as written. */
function htmlWithout(body: string, key: string, names: readonly string[]): [string, string | null] {
  // Tags are looked for outside comments and hidden elements, as the server's tree finds them.
  const scan = masked(body)
  const spans = issueSpans(scan)
  const cuts: [number, number][] = []
  let moved: string | null = null
  for (const found of scan.matchAll(/<li\b[^>]*>/gi)) {
    const start = found.index
    if (!spans.some(([from, to]) => from < start && start < to)) continue
    if (cuts.length && start < cuts.at(-1)![1]) continue
    const end = itemEnd(scan, start + found[0].length)
    const leading = body.slice(start + found[0].length, end).replace(COMMENT, '').replace(HIDDEN_ELEMENT, '\n').replace(/<[^>]*>/g, '').trimStart()
    const first = keysIn(leading, names)[0]
    if (!first || first.index !== 0 || first.key !== key) continue
    moved ??= `${body.slice(start, end).trimEnd()}</li>`
    const closeTag = /^<\/li\s*>/i.exec(scan.slice(end))
    const close = closeTag ? end + closeTag[0].length : end
    const lineStart = body.lastIndexOf('\n', start - 1) + 1
    const ownLine = /^[ \t]*$/.test(body.slice(lineStart, start)) && body[close] === '\n'
    cuts.push(ownLine ? [lineStart, close + 1] : [start, close])
  }
  let out = ''
  let from = 0
  for (const [start, end] of cuts) {
    out += body.slice(from, start)
    from = end
  }
  return [out + body.slice(from), moved]
}

const othersThan = (listing: PlanListing[], key: string) => JSON.stringify(listing.filter((one) => one.key !== key))

/** The body without the items that list `key`, every other byte kept: the mock's `unlist_from_plan`. */
export function unlisted(body: string, key: string, names: readonly string[]): Placed {
  const before = planListing(body, names)
  if (!before.some((one) => one.key === key)) return 'not_listed'
  const [after] = isHtmlBody(body) ? htmlWithout(body, key, names) : markdownWithout(body, key, names)
  const now = planListing(after, names)
  return now.some((one) => one.key === key) || othersThan(now, key) !== othersThan(before, key) ? 'refused' : { body: after }
}

/** The body with `key`'s item moved, as written, under the phase `section` begins: the mock's `move_in_plan`. */
export function moved(body: string, key: string, title: string, section: string, names: readonly string[]): Placed {
  const before = planListing(body, names)
  if (!before.some((one) => one.key === key)) return 'not_listed'
  const html = isHtmlBody(body)
  const [rest, item] = html ? htmlWithout(body, key, names) : markdownWithout(body, key, names)
  const after = item === null ? withItem(rest, key, title, section, names) : html ? htmlWithLi(rest, item, section) : markdownWith(rest, item, section, names)
  const now = planListing(after, names)
  // The issue must end up in the phase named, as the heading was picked.
  const wanted = words(section).slice(0, PHASE_NAME_MAX)
  const placed = now.some((one) => one.key === key && one.section !== null && namesPhase(one.section, wanted, false))
  return !placed || othersThan(now, key) !== othersThan(before, key) ? 'refused' : { body: after }
}
